import { useEffect, useLayoutEffect, useRef, useState } from "react";

// Matches Calendar.module.scss's .custom-calendar fixed width (26.625rem).
// Positioning is computed from this so a narrow trigger (e.g. one cell of a
// multi-column grid or a tight flex row) never gets a wider popover
// clipped by, or overlapping, a sibling field.
const CALENDAR_WIDTH = 426;
// Rough height of a 6-week month layout, used only as the first-paint guess
// before the real size is measured (see the layout effect below) — actual
// height varies with the number of weeks a given month spans.
const CALENDAR_HEIGHT_ESTIMATE = 460;
const VIEWPORT_MARGIN = 12;
const TRIGGER_GAP = 4;

// Given the trigger's rect and popup dimensions (estimated or measured),
// picks a viewport-clamped position: flips to the trigger's right edge when
// there's no room to the right, and flips above the trigger when there's no
// room below.
const computePosition = (rect, calendarWidth, calendarHeight) => {
  const left =
    rect.left + calendarWidth > window.innerWidth - VIEWPORT_MARGIN
      ? rect.right - calendarWidth
      : rect.left;
  const maxLeft = window.innerWidth - calendarWidth - VIEWPORT_MARGIN;

  const fitsBelow =
    rect.bottom + TRIGGER_GAP + calendarHeight <=
    window.innerHeight - VIEWPORT_MARGIN;
  const top = fitsBelow
    ? rect.bottom + TRIGGER_GAP
    : Math.max(VIEWPORT_MARGIN, rect.top - TRIGGER_GAP - calendarHeight);

  return {
    top,
    left: Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft)),
  };
};

// Drives a Calendar rendered as a portaled dropdown: computes a
// viewport-clamped fixed position from the trigger's bounding rect (flips
// horizontally when there's no room to the right, flips above the trigger
// when there's no room below), and handles outside-click/scroll/resize
// close. The calendar itself is portaled to document.body by
// CalendarPopover, so it is not a DOM descendant of the trigger — callers
// must attach wrapperRef (the field's outer container) and calendarRef
// (passed through to CalendarPopover) so outside-click detection can
// account for both.
export const useCalendarDropdown = ({ width = CALENDAR_WIDTH } = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const calendarRef = useRef(null);
  const triggerRectRef = useRef(null);

  const toggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      triggerRectRef.current = rect;
      setPosition(computePosition(rect, width, CALENDAR_HEIGHT_ESTIMATE));
    }
    setIsOpen((v) => !v);
  };

  const close = () => setIsOpen(false);

  // Measure the whole popup, including any preset sidebar, and reposition
  // when calendar navigation changes its size (five vs. six week months).
  useLayoutEffect(() => {
    if (!isOpen || !calendarRef.current || !triggerRectRef.current) return undefined;
    const updatePosition = () => {
      const bounds = calendarRef.current.getBoundingClientRect();
      const next = computePosition(
        triggerRectRef.current,
        bounds.width || width,
        bounds.height || CALENDAR_HEIGHT_ESTIMATE,
      );
      setPosition((previous) =>
        previous.top === next.top && previous.left === next.left
          ? previous
          : next,
      );
    };
    updatePosition();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updatePosition);
    observer.observe(calendarRef.current);
    return () => observer.disconnect();
  }, [isOpen, width]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    // A fixed-position popup computed once at open time would drift away
    // from its trigger if the surrounding content scrolls or the viewport
    // resizes, so just close it instead of tracking position live.
    const handleReposition = (event) => {
      if (
        event.type === "scroll" &&
        event.target instanceof Node &&
        calendarRef.current?.contains(event.target)
      ) return;
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen]);

  return {
    isOpen,
    position,
    wrapperRef,
    triggerRef,
    calendarRef,
    toggle,
    close,
  };
};
